import {useQuery} from "@apollo/client";
import {ErrorBoundary} from "@sentry/react";
import {BarEventStatus} from "@uptime-com/uptimebar";
import {useState} from "react";

import {Card} from "@/components/card/Card.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {CheckDetailsAlert, CheckDetailsResponse, GQL_CHECK_DETAILS} from "@/lib/queries/checks.ts";
import {CheckOverviewProps} from "@/pages/checks/analysis/overview/checktypes/CheckOverview.tsx";
import {CloudStatusIncidentHistory} from "@/pages/checks/analysis/overview/CloudStatusIncidentHistory.tsx";
import {CloudStatusIncidentHistoryHeader} from "@/pages/checks/analysis/overview/CloudStatusIncidentHistoryHeader.tsx";
import {CloudStatusSocialLinks} from "@/pages/checks/analysis/overview/CloudStatusSocialLinks.tsx";
import {CloudStatusV2IncidentHistory} from "@/pages/checks/analysis/overview/CloudStatusV2IncidentHistory.tsx";
import {getCheckState} from "@/pages/checks/analysis/statsHeader/utils.ts";
import {CheckStatus} from "@/pages/checks/components/details/CheckStatus.tsx";
import {UptimeBarCheckAnalysis} from "@/pages/checks/components/details/UptimeBar/UptimeBarCheckAnalysis.tsx";

import {CheckInfoBoxes} from "./CheckInfoBoxes";

export const CloudStatusOverview = ({
  checkId,
  start,
  end,
  cloudStatus,
  thirdPartyGroup,
  interval,
  intervals,
  createdAt,
  is_paused,
  cached_state_is_up,
  latestDowntime,
  responseTimes,
  uptimeBarEvents,
}: CheckOverviewProps) => {
  const {userHasFeature} = useCurrentUserContext();
  const hasCloudStatusV2 = userHasFeature("cloudstatus_v2");

  const [sortingDirection, setSortingDirection] = useState<SortingDirection>(SortingDirection.Desc);
  const sorting: Sorting = {column: "date", direction: sortingDirection};
  const setSorting = (s: Sorting) => setSortingDirection(s.direction);

  const intervalAlerts = intervals.reduce((acc: CheckDetailsAlert[], item) => {
    return acc.concat(item.alerts);
  }, []);

  let since = createdAt.toISOString();
  const state = getCheckState({state_is_up: cached_state_is_up, is_paused: is_paused, created_at: since});

  const {data, loading, previousData} = useQuery<CheckDetailsResponse>(GQL_CHECK_DETAILS, {
    variables: {
      id: checkId,
      start: start,
      end: end,
    },
  });

  const renderData = loading ? previousData : data;
  const {stats} = renderData ? renderData : {};

  if (latestDowntime?.is_currently_down) {
    since = latestDowntime!.created_at.toString();
  } else if (latestDowntime && latestDowntime.ended_at) {
    since = latestDowntime!.ended_at.toString();
  }

  return (
    <div className="d-flex flex-column gap-4">
      <CloudStatusSocialLinks component={thirdPartyGroup ?? cloudStatus} />
      <Card variant="cloudStatus" className="row">
        <div className="d-flex gap-3 mb-2 flex-wrap flex-md-nowrap">
          <div>
            <CheckStatus state={state} uptimePercentage={stats?.totals.uptime_pct || 0} />
          </div>
          <div className="w-100">
            <ErrorBoundary>
              <UptimeBarCheckAnalysis
                start={start}
                end={end}
                interval={interval}
                createdAt={createdAt}
                responseTimes={responseTimes}
                intervalAlerts={intervalAlerts}
                uptimeBarEvents={uptimeBarEvents}
                noResponseTimesDefaultStatus={BarEventStatus.UP}
              />
            </ErrorBoundary>
          </div>
        </div>
        <CheckInfoBoxes
          state={state}
          since={since}
          outages={stats?.totals.outages}
          downtimeSecs={stats?.totals.downtime_secs}
          latestDowntime={latestDowntime}
        />
      </Card>
      {hasCloudStatusV2 ? (
        <CloudStatusV2IncidentHistory checkId={checkId} start={start} end={end} />
      ) : (
        <Card variant="cloudStatus" className="row">
          <CloudStatusIncidentHistoryHeader sorting={sorting} setSorting={setSorting} />
          <div className="mt-3">
            {cloudStatus ? (
              <CloudStatusIncidentHistory
                cloudStatus={cloudStatus}
                start={start}
                end={end}
                direction={sortingDirection}
              />
            ) : (
              <div className="pulse-color-gray-700">No incidents found.</div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};
